import React, { useState, useEffect, useCallback } from 'react';
import { 
  Activity, AlertTriangle, DollarSign, 
  TrendingUp, TrendingDown, Minus, CheckCircle,
  Plus, Wrench, FileText, ChevronRight,
  Car, FolderOpen, Camera
} from 'lucide-react';

// Import the new tab components
import VehicleDetailsTab from './VehicleDetailsTab';
import Badge from './common/Badge';
import MarketValue from './MarketValue';
import QuickIssueReport from './QuickIssueReport';
import VehicleIssuesTab from './VehicleIssuesTab';
import VehicleDocumentsTab from './VehicleDocumentsTab';
import { api, vehicleHealthAPI } from '../services/api';
import VehicleMaintenance from './VehicleMaintenance';

const VehicleHealthDashboard = ({ vehicle, profilePicture: initialProfilePicture, onBack }) => {
  const [healthData, setHealthData] = useState(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [maintenanceSubTab, setMaintenanceSubTab] = useState('schedule');
  const [vehicleIssues, setVehicleIssues] = useState([]);
  const [profilePicture, setProfilePicture] = useState(initialProfilePicture);

  const calculateFallbackHealthData = useCallback(() => {
    // Calculate basic health score based on available vehicle data
    let score = 100;
    const factors = {
      unresolved_issues: 0,
      critical_issues: 0,
      major_issues: 0,
      overdue_maintenance: 0
    };
    
    const recommendations = [];
    const next_actions = [];

    // Check vehicle age and mileage for basic recommendations
    if (vehicle?.year) {
      const currentYear = new Date().getFullYear();
      const vehicleAge = currentYear - vehicle.year;
      
      if (vehicleAge > 10) {
        score -= 15;
        factors['vehicle_age'] = `${vehicleAge} years`;
        recommendations.push('Consider comprehensive inspection due to vehicle age');
        next_actions.push({
          action: 'Schedule comprehensive inspection',
          urgency: 'medium',
          estimated_cost: '$100-200'
        });
      }
    }

    // Check mileage for maintenance recommendations
    if (vehicle?.mileage) {
      if (vehicle.mileage > 100000) {
        score -= 20;
        factors['high_mileage'] = `${vehicle.mileage.toLocaleString()} miles`;
        recommendations.push('High mileage vehicle - schedule major service inspection');
        next_actions.push({
          action: 'Major service inspection',
          urgency: 'high',
          estimated_cost: '$200-500'
        });
      } else if (vehicle.mileage > 50000) {
        score -= 10;
        factors['moderate_mileage'] = `${vehicle.mileage.toLocaleString()} miles`;
        recommendations.push('Schedule routine maintenance check');
        next_actions.push({
          action: 'Routine maintenance check',
          urgency: 'medium',
          estimated_cost: '$50-150'
        });
      }
    }

    // Add common maintenance recommendations based on vehicle type
    if (vehicle?.make && vehicle?.model) {
      const make = vehicle.make.toLowerCase();
      
      if (make === 'honda' || make === 'toyota') {
        recommendations.push('Check timing belt/chain at recommended intervals');
        recommendations.push('Monitor oil consumption - common in higher mileage vehicles');
      } else if (make === 'bmw' || make === 'mercedes') {
        recommendations.push('Regular fluid checks and replacements recommended');
        recommendations.push('Monitor for common electrical system issues');
      } else if (make === 'ford' || make === 'chevrolet') {
        recommendations.push('Check transmission fluid and filter regularly');
        recommendations.push('Monitor for common engine sensor issues');
      }
    }

    // Ensure score is within bounds
    score = Math.max(0, Math.min(100, score));

    // Determine status and trend
    let status = 'good';
    if (score >= 90) status = 'excellent';
    else if (score >= 70) status = 'good';
    else if (score >= 50) status = 'fair';
    else if (score >= 30) status = 'poor';
    else status = 'critical';

    return {
      score,
      status,
      trend: 'stable',
      factors,
      recommendations: recommendations.slice(0, 5), // Limit to 5 recommendations
      next_actions: next_actions.slice(0, 3), // Limit to 3 actions
      calculated_at: new Date().toISOString()
    };
  }, [vehicle]);

  const loadHealthData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
  
      // Load vehicle health score, maintenance data, and vehicle issues in parallel
      const [healthResponse, maintenanceLogsResponse, maintenanceScheduleResponse, vehicleIssuesResponse] = await Promise.allSettled([
        vehicleHealthAPI.getHealthScore(vehicle.id),
        // Load both resolved and unresolved maintenance logs for complete history
        vehicleHealthAPI.getMaintenanceLogs(vehicle.id, { resolved: null }), // null means all logs
        // Use the new maintenance API endpoint with vehicle profile ID
        api.get(`/vehicle/maintenance/${vehicle.id}`, {
          params: { force_refresh: false }
        }).then(r => r.data).catch(() => null),
        // Load vehicle issues to include in health factors and service history
        vehicleHealthAPI.getVehicleIssues(vehicle.id)
      ]);
  
      // Process health score data
      if (healthResponse.status === 'fulfilled' && healthResponse.value) {
        setHealthData(healthResponse.value);
      } else {
        // Fallback to calculated health data if API fails
        setHealthData(calculateFallbackHealthData());
      }
  
      // Process maintenance logs - ensure it's always an array
      if (maintenanceLogsResponse.status === 'fulfilled' && maintenanceLogsResponse.value) {
        const logs = maintenanceLogsResponse.value;
        setMaintenanceLogs(Array.isArray(logs) ? logs : []);
      } else {
        setMaintenanceLogs([]);
      }
  
      // Process maintenance schedule with proper structure
      if (maintenanceScheduleResponse.status === 'fulfilled' && maintenanceScheduleResponse.value) {
        // Ensure the data has the expected structure
        const scheduleData = maintenanceScheduleResponse.value;
        setMaintenanceSchedule({
          upcoming_services: scheduleData.upcoming_services || [],
          overdue_services: scheduleData.overdue_services || [],
          maintenance_schedule: scheduleData.maintenance_schedule || [],
          service_categories: scheduleData.service_categories || {},
          maintenance_cost_analysis: scheduleData.maintenance_cost_analysis || {},
          next_major_service: scheduleData.next_major_service || null,
          completed_services: scheduleData.completed_services || [],
          manufacturer_recommendations: scheduleData.manufacturer_recommendations || [],
          upgrade_required: scheduleData.upgrade_required || false,
          message: scheduleData.message || null,
          vehicle_info: scheduleData.vehicle_info || {},
          data_source: scheduleData.data_source || 'unknown'
        });
      } else {
        setMaintenanceSchedule(null);
      }
  
      // Process vehicle issues - ensure it's always an array
      if (vehicleIssuesResponse.status === 'fulfilled' && vehicleIssuesResponse.value) {
        const issues = vehicleIssuesResponse.value;
        setVehicleIssues(Array.isArray(issues) ? issues : []);
      } else {
        setVehicleIssues([]);
      }
  
    } catch (err) {
      console.error('Error loading health data:', err);
      setError('Failed to load vehicle health data');
      // Set fallback data
      setHealthData(calculateFallbackHealthData());
      setMaintenanceLogs([]);
      setMaintenanceSchedule(null);
      setVehicleIssues([]);
    } finally {
      setLoading(false);
    }
  }, [vehicle, calculateFallbackHealthData]);

  // Function to refresh health data (can be called from child components)
  const refreshHealthData = useCallback(() => {
    loadHealthData();
  }, [loadHealthData]);

  useEffect(() => {
    if (vehicle?.id) {
      loadHealthData();
    }
  }, [vehicle, loadHealthData]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-danger" />;
      default:
        return <Minus className="w-4 h-4 text-ink-muted" />;
    }
  };

  // Status is a tinted badge, never a fill (DESIGN_SYSTEM.md: semantic
  // colours are badges only). This card was a full-width green/orange/red
  // gradient with white text and a 6xl emoji.
  const getStatusBadge = (status) => {
    switch (status) {
      case 'excellent':
      case 'good':
        return 'success';
      case 'fair':
        return 'warning';
      case 'poor':
      case 'critical':
        return 'danger';
      // #136: no data at all is neither good news nor an alarm -- it must
      // never render the way a real "good" score does.
      case 'insufficient_data':
        return 'neutral';
      default:
        return 'success';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'excellent':
        return 'Excellent';
      case 'good':
        return 'Good';
      case 'fair':
        return 'Fair';
      case 'poor':
        return 'Poor';
      case 'critical':
        return 'Critical';
      case 'insufficient_data':
        return 'Not enough data';
      default:
        return 'Good';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <span className="ml-2 text-ink-muted">Loading vehicle data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-danger-tint border border-danger-border rounded-lg p-4">
        <p className="text-danger">{error}</p>
        <button onClick={loadHealthData} className="mt-2 text-danger hover:text-danger">
          Try Again
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'details', label: 'Vehicle Details', icon: Car },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'value', label: 'Value & Costs', icon: DollarSign },
    { id: 'history', label: 'History', icon: FileText },
    { id: 'documents', label: 'Documents', icon: FolderOpen }
  ];

  // Ensure maintenanceLogs and vehicleIssues are arrays before filtering
  const safeMaintenanceLogs = Array.isArray(maintenanceLogs) ? maintenanceLogs : [];
  const safeVehicleIssues = Array.isArray(vehicleIssues) ? vehicleIssues : [];

  const unresolvedIssues = safeMaintenanceLogs.filter(log => !log.resolved);
  const criticalIssues = unresolvedIssues.filter(log => log.severity === 'critical');
  const resolvedIssues = safeMaintenanceLogs.filter(log => log.resolved);

  // Get unresolved vehicle issues (status !== 'resolved')
  const unresolvedVehicleIssues = safeVehicleIssues.filter(issue => issue.status !== 'resolved');
  const criticalVehicleIssues = unresolvedVehicleIssues.filter(issue => issue.severity === 'critical');
  const majorVehicleIssues = unresolvedVehicleIssues.filter(issue => issue.severity === 'major');

  // Calculate real health factors from actual data
  const healthFactors = healthData?.factors || {};
  const realHealthFactors = {
    unresolved_issues: unresolvedIssues.length + unresolvedVehicleIssues.length,
    critical_issues: criticalIssues.length + criticalVehicleIssues.length,
    major_issues: unresolvedIssues.filter(log => log.severity === 'major').length + majorVehicleIssues.length,
    overdue_maintenance: maintenanceSchedule?.overdue_services?.length || 0,
    upcoming_maintenance: maintenanceSchedule?.upcoming_services?.filter(s => s.miles_until_due <= 1000).length || 0,
    total_upcoming: maintenanceSchedule?.upcoming_services?.length || 0
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button onClick={onBack} className="text-accent hover:text-accent-hover mb-4">
          ← Back to Vehicles
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            {profilePicture && (
              <img
                src={profilePicture.url || profilePicture}
                alt="Vehicle profile"
                className="w-16 h-16 rounded-lg object-cover border-2 border-border-default"
              />
            )}
            <div>
              <h1 className="text-page-title font-bold text-ink-soft">
                {vehicle.display_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              </h1>
              <p className="text-ink-muted mt-1">
                {vehicle.mileage?.toLocaleString()} miles • VIN: {vehicle.vin || 'Not provided'}
              </p>
              {vehicle.zip_code && (
                <p className="text-body text-ink-muted">Location: ZIP {vehicle.zip_code}</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowIssueForm(!showIssueForm)}
            className="flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
          >
            <Plus className="w-4 h-4 mr-2" />
            Report Issue
          </button>
        </div>
      </div>

      {/* Issue Report Form */}
      {showIssueForm && (
        <div className="mb-6">
          <QuickIssueReport
            vehicleId={vehicle.id}
            currentMileage={vehicle.mileage || 0}
            onSuccess={() => {
              setShowIssueForm(false);
              loadHealthData(); // Refresh health data after reporting issue
            }}
            onCancel={() => setShowIssueForm(false)}
          />
        </div>
      )}



      {/* Health Score Card */}
      {healthData && (
        <div className="rounded-card border border-border-default bg-surface-card p-5 sm:p-6 mb-6">
          <h2 className="text-label uppercase text-ink-muted mb-3">Vehicle Health Score</h2>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <div className="text-5xl font-mono tabular-nums font-semibold text-ink">
              {healthData.score === null || healthData.score === undefined ? '—' : healthData.score}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge status={getStatusBadge(healthData.status)}>{getStatusText(healthData.status)}</Badge>
                {getTrendIcon(healthData.trend)}
              </div>
              <p className="text-body text-ink-muted mt-1">
                {healthData.trend === 'improving' ? 'Trending better' :
                 healthData.trend === 'declining' ? 'Needs attention' :
                 'Stable condition'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Critical Alerts */}
      {criticalIssues.length > 0 && (
        <div className="bg-danger-tint border-2 border-danger-border rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-danger mt-0.5 mr-3" />
            <div className="flex-1">
              <h3 className="font-semibold text-danger">Critical Issues Require Immediate Attention</h3>
              <ul className="mt-2 space-y-1">
                {criticalIssues.map((issue, idx) => (
                  <li key={idx} className="text-danger text-body">
                    • {issue.description}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}


      {/* Enhanced Tabs */}
      <div className="border-b border-border-default mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-body flex items-center gap-2 whitespace-nowrap min-h-touch ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-ink-muted hover:text-ink-soft'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && healthData && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Existing Key Metrics card */}
        <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
          <h3 className="text-card-title font-semibold text-ink-soft mb-4">Health Factors</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Unresolved Issues</span>
              <span className="font-semibold">{realHealthFactors.unresolved_issues}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Critical Issues</span>
              <span className="font-semibold text-danger">{realHealthFactors.critical_issues}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Major Issues</span>
              <span className="font-semibold text-warning">{realHealthFactors.major_issues}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Overdue Maintenance</span>
              <span className="font-semibold text-danger">{realHealthFactors.overdue_maintenance}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-ink-muted">Services Due Soon</span>
              <span className="font-semibold text-warning">{realHealthFactors.upcoming_maintenance}</span>
            </div>
            {healthFactors.vehicle_age && (
              <div className="flex justify-between items-center">
                <span className="text-ink-muted">Vehicle Age</span>
                <span className="font-semibold text-accent">{healthFactors.vehicle_age}</span>
              </div>
            )}
            {healthFactors.high_mileage && (
              <div className="flex justify-between items-center">
                <span className="text-ink-muted">High Mileage</span>
                <span className="font-semibold text-warning">{healthFactors.high_mileage}</span>
              </div>
            )}
          </div>
        </div>

          {/* AI Recommendations */}
          <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
            <h3 className="text-card-title font-semibold text-ink-soft mb-4">AI Recommendations</h3>
            <div className="space-y-2">
              {healthData.recommendations && healthData.recommendations.length > 0 ? (
                healthData.recommendations.slice(0, 4).map((rec, idx) => (
                  <div key={idx} className="flex items-start">
                    <ChevronRight className="w-4 h-4 text-ink-muted mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-body text-ink-soft">{rec}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <CheckCircle className="w-8 h-8 text-success mx-auto mb-2" />
                  <p className="text-body text-ink-muted">No immediate recommendations</p>
                  <p className="text-small text-ink-muted">Vehicle is in good condition</p>
                </div>
              )}
            </div>
          </div>

          {/* Next Actions */}
          {healthData.next_actions && healthData.next_actions.length > 0 && (
            <div className="md:col-span-2 bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
              <h3 className="text-card-title font-semibold text-ink-soft mb-4">Recommended Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {healthData.next_actions.map((action, idx) => (
                  <div key={idx} className="border border-border-default rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-body font-medium text-ink-soft">{action.action}</span>
                      <span className={`px-2 py-1 text-small rounded-full ${
                        action.urgency === 'high' ? 'bg-danger-tint text-danger' :
                        action.urgency === 'medium' ? 'bg-warning-tint text-warning' :
                        'bg-success-tint text-success'
                      }`}>
                        {action.urgency}
                      </span>
                    </div>
                    {action.estimated_cost && (
                      <p className="text-body text-ink-muted">Est. Cost: {action.estimated_cost}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Maintenance Quick View card */}
    <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
      <h3 className="text-card-title font-semibold text-ink-soft mb-4">Maintenance Summary</h3>
      {maintenanceSchedule ? (
        <div className="space-y-3">
          {/* Next Major Service */}
          {maintenanceSchedule.next_major_service && (
            <div className="p-3 bg-warning-tint rounded-lg">
              <p className="text-body font-medium text-warning">Next Major Service</p>
              <p className="text-small text-warning mt-1">
                {maintenanceSchedule.next_major_service.services?.join(', ')} 
                in {maintenanceSchedule.next_major_service.miles_away?.toLocaleString()} miles
              </p>
              <p className="text-small text-warning mt-1">
                Est. ${maintenanceSchedule.next_major_service.estimated_cost?.low}-${maintenanceSchedule.next_major_service.estimated_cost?.high}
              </p>
            </div>
          )}
          
          {/* Cost Analysis Preview */}
          {maintenanceSchedule.maintenance_cost_analysis && (
            <div className="grid grid-cols-2 gap-2 text-body">
              <div className="text-center p-2 bg-surface-page rounded">
                <p className="text-small text-ink-muted">Next Year</p>
                <p className="font-semibold">${maintenanceSchedule.maintenance_cost_analysis.next_year_estimate?.toLocaleString() || 0}</p>
              </div>
              <div className="text-center p-2 bg-surface-page rounded">
                <p className="text-small text-ink-muted">Cost/Mile</p>
                <p className="font-semibold">${maintenanceSchedule.maintenance_cost_analysis.cost_per_mile?.toFixed(3) || 0}</p>
              </div>
            </div>
          )}
          
          {/* Quick Actions */}
          <button 
            onClick={() => {
              setActiveTab('maintenance');
              setMaintenanceSubTab('schedule');
            }}
            className="w-full text-center text-body text-accent hover:text-accent-hover py-2 border border-accent rounded hover:bg-accent-tint transition"
          >
            View Full Maintenance Schedule →
          </button>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-body text-ink-muted mb-2">No maintenance data available</p>
          <button 
            onClick={() => setActiveTab('maintenance')}
            className="text-body text-accent hover:text-accent-hover"
          >
            Set up maintenance tracking →
          </button>
        </div>
      )}
    </div>
              </div>
          )}
      {activeTab === 'details' && (
        <VehicleDetailsTab vehicle={vehicle} />
      )}

      {activeTab === 'maintenance' && (
        <div>
          {/* Maintenance Sub-tabs */}
          <div className="border-b border-border-default mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setMaintenanceSubTab('schedule')}
                className={`py-2 px-1 border-b-2 font-medium text-body flex items-center gap-2 min-h-touch ${
                  maintenanceSubTab === 'schedule'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink-soft'
                }`}
              >
                <Wrench className="w-4 h-4" />
                Maintenance Schedule
                {maintenanceSchedule?.overdue_services?.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-small bg-danger-tint text-danger rounded-full">
                    {maintenanceSchedule.overdue_services.length} overdue
                  </span>
                )}
              </button>
              <button
                onClick={() => setMaintenanceSubTab('issues')}
                className={`py-2 px-1 border-b-2 font-medium text-body flex items-center gap-2 min-h-touch ${
                  maintenanceSubTab === 'issues'
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink-soft'
                }`}
              >
                <AlertTriangle className="w-4 h-4" />
                Issues
                {unresolvedIssues.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-small bg-danger-tint text-danger rounded-full">
                    {unresolvedIssues.length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Maintenance Sub-tab Content */}
          {maintenanceSubTab === 'schedule' && (
            // Use the comprehensive VehicleMaintenance component we built
            <VehicleMaintenance 
              vehicleProfileId={vehicle.id}
              vin={vehicle.vin || null}
              year={vehicle.year || null}
              make={vehicle.make || null}
              model={vehicle.model || null}
              trim={vehicle.trim || null}
              initialMileage={vehicle.mileage || 0}
              onMileageUpdate={(newMileage) => {
                // Update vehicle mileage in parent component
                vehicle.mileage = newMileage;
                loadHealthData(); // Refresh health data with new mileage
              }}
            />
          )}

          {maintenanceSubTab === 'issues' && (
            <VehicleIssuesTab 
              vehicleId={vehicle.id}
              vehicleInfo={{
                year: vehicle.year,
                make: vehicle.make,
                model: vehicle.model,
                trim: vehicle.trim,
                vin: vehicle.vin,
                mileage: vehicle.mileage || 0
              }}
              onIssueUpdate={refreshHealthData}
            />
          )}
        </div>
      )}

      {activeTab === 'value' && (
        <MarketValue 
          vehicleId={vehicle.id} 
          initialMileage={vehicle.mileage || 0} 
          initialZipCode={vehicle.zip_code || null} 
        />
      )}

      {activeTab === 'history' && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
          <h3 className="text-card-title font-semibold text-ink-soft mb-4">Service History</h3>
          
          {/* Summary Stats */}
          {safeMaintenanceLogs.length > 0 && (
            <div className="mb-6 p-4 bg-surface-page rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-body">
                <div className="text-center">
                  <p className="text-ink-muted">Total Services</p>
                  <p className="font-semibold text-card-title">{safeMaintenanceLogs.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Resolved</p>
                  <p className="font-semibold text-success">
                    {resolvedIssues.length}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Unresolved</p>
                  <p className="font-semibold text-warning">
                    {unresolvedIssues.length}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Total Cost</p>
                  <p className="font-semibold text-card-title">
                    ${safeMaintenanceLogs
                      .filter(log => log.cost)
                      .reduce((sum, log) => sum + (log.cost || 0), 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Vehicle Issues Summary */}
          {safeVehicleIssues.length > 0 && (
            <div className="mb-6 p-4 bg-accent-tint rounded-lg">
              <h4 className="text-md font-semibold text-accent mb-3">Vehicle Issues Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-body">
                <div className="text-center">
                  <p className="text-ink-muted">Total Issues</p>
                  <p className="font-semibold text-card-title">{safeVehicleIssues.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Resolved</p>
                  <p className="font-semibold text-success">
                    {safeVehicleIssues.filter(issue => issue.status === 'resolved').length}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Unresolved</p>
                  <p className="font-semibold text-warning">
                    {unresolvedVehicleIssues.length}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-ink-muted">Total Cost</p>
                  <p className="font-semibold text-card-title">
                    ${safeVehicleIssues
                      .filter(issue => issue.repair_cost)
                      .reduce((sum, issue) => sum + (issue.repair_cost || 0), 0)
                      .toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Unresolved Issues */}
          {unresolvedIssues.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-warning mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Unresolved Issues ({unresolvedIssues.length})
              </h4>
              <div className="space-y-3">
                {unresolvedIssues.map((log, idx) => (
                  <div key={`unresolved-${idx}`} className="flex items-center justify-between p-3 border border-warning rounded-lg bg-warning-tint">
                    <div className="flex-1">
                      <p className="font-medium text-ink-soft">{log.description}</p>
                      <div className="flex items-center space-x-4 text-body text-ink-muted mt-1">
                        <span>{new Date(log.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{log.mileage_at_log?.toLocaleString()} miles</span>
                        {log.service_provider && (
                          <>
                            <span>•</span>
                            <span>{log.service_provider}</span>
                          </>
                        )}
                      </div>
                      {log.notes && (
                        <p className="text-body text-ink-muted mt-2 italic">"{log.notes}"</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Cost Display */}
                      {log.cost && (
                        <div className="text-right">
                          <p className="text-body text-ink-muted">Cost</p>
                          <p className="font-semibold text-success">${log.cost}</p>
                        </div>
                      )}
                      
                      {/* Severity Badge */}
                      <span className={`px-2 py-1 text-small rounded-full ${
                        log.severity === 'critical' ? 'bg-danger-tint text-danger' :
                        log.severity === 'major' ? 'bg-warning-tint text-warning' :
                        log.severity === 'moderate' ? 'bg-warning-tint text-warning' :
                        'bg-accent-tint text-accent'
                      }`}>
                        {log.severity || 'info'}
                      </span>
                      
                      {/* Resolution Status */}
                      <AlertTriangle className="w-4 h-4 text-warning" title="Unresolved" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Resolved Issues */}
          {resolvedIssues.length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-success mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolved Maintenance Issues ({resolvedIssues.length})
              </h4>
              <div className="space-y-3">
                {resolvedIssues.map((log, idx) => (
                  <div key={`resolved-${idx}`} className="flex items-center justify-between p-3 border border-border-default rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-ink-soft">{log.description}</p>
                      <div className="flex items-center space-x-4 text-body text-ink-muted mt-1">
                        <span>{new Date(log.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{log.mileage_at_log?.toLocaleString()} miles</span>
                        {log.service_provider && (
                          <>
                            <span>•</span>
                            <span>{log.service_provider}</span>
                          </>
                        )}
                      </div>
                      {log.notes && (
                        <p className="text-body text-ink-muted mt-2 italic">"{log.notes}"</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Cost Display */}
                      {log.cost && (
                        <div className="text-right">
                          <p className="text-body text-ink-muted">Cost</p>
                          <p className="font-semibold text-success">${log.cost}</p>
                        </div>
                      )}
                      
                      {/* Severity Badge */}
                      <span className={`px-2 py-1 text-small rounded-full ${
                        log.severity === 'critical' ? 'bg-danger-tint text-danger' :
                        log.severity === 'major' ? 'bg-warning-tint text-warning' :
                        log.severity === 'moderate' ? 'bg-warning-tint text-warning' :
                        'bg-accent-tint text-accent'
                      }`}>
                        {log.severity || 'info'}
                      </span>
                      
                      {/* Resolution Status */}
                      <CheckCircle className="w-4 h-4 text-success" title="Resolved" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Resolved Vehicle Issues */}
          {safeVehicleIssues.filter(issue => issue.status === 'resolved').length > 0 && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-success mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Resolved Vehicle Issues ({safeVehicleIssues.filter(issue => issue.status === 'resolved').length})
              </h4>
              <div className="space-y-3">
                {safeVehicleIssues
                  .filter(issue => issue.status === 'resolved')
                  .map((issue, idx) => (
                  <div key={`resolved-vehicle-${idx}`} className="flex items-center justify-between p-3 border border-border-default rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-ink-soft">{issue.title}</p>
                      <div className="flex items-center space-x-4 text-body text-ink-muted mt-1">
                        <span>{new Date(issue.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{issue.current_mileage?.toLocaleString()} miles</span>
                        {issue.category && (
                          <>
                            <span>•</span>
                            <span>{issue.category}</span>
                          </>
                        )}
                      </div>
                      {issue.description && (
                        <p className="text-body text-ink-muted mt-2 italic">"{issue.description}"</p>
                      )}
                      {issue.resolution_notes && (
                        <p className="text-body text-ink-muted mt-2 italic">Resolution: "{issue.resolution_notes}"</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Cost Display */}
                      {issue.repair_cost && (
                        <div className="text-right">
                          <p className="text-body text-ink-muted">Cost</p>
                          <p className="font-semibold text-success">${issue.repair_cost}</p>
                        </div>
                      )}
                      
                      {/* Severity Badge */}
                      <span className={`px-2 py-1 text-small rounded-full ${
                        issue.severity === 'critical' ? 'bg-danger-tint text-danger' :
                        issue.severity === 'high' ? 'bg-warning-tint text-warning' :
                        issue.severity === 'medium' ? 'bg-warning-tint text-warning' :
                        'bg-accent-tint text-accent'
                      }`}>
                        {issue.severity || 'info'}
                      </span>
                      
                      {/* Resolution Status */}
                      <CheckCircle className="w-4 h-4 text-success" title="Resolved" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {safeMaintenanceLogs.length === 0 && (
            <p className="text-ink-muted">No maintenance history available yet.</p>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <VehicleDocumentsTab 
          vehicle={vehicle} 
          onProfilePictureUpdate={setProfilePicture}
        />
      )}
    </div>
  );
};

export default VehicleHealthDashboard;